import React from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import type { Location } from '../../api/config';

/**
 * Fulfillment / shipping fields. `uses_stock_truck` swaps between two
 * mutually-exclusive sub-fields:
 *   - stock_truck_delivery_day (Select) when stock truck is on
 *   - ground_transit_business_days (number) when stock truck is off
 * Stock truck route + freight option are siblings.
 *
 * The auto-fill side effect for `quote_confirmation_deadline` (the modal's
 * "Auto-filled from send day" UX) lives at the wrapper layer; this component
 * just exposes a `deadlineHelper` override so the wrapper can swap the helper
 * text + color when the flag is set.
 *
 * sc-6506: the deadline input renders only while `show_reply_by_time` is on.
 * Turning the toggle off hides the field but never clears its value.
 */
export type FulfillmentValue = Pick<
  Location,
  | 'show_reply_by_time'
  | 'quote_confirmation_deadline'
  | 'collect_account_info'
  | 'uses_stock_truck'
  | 'stock_truck_delivery_day'
  | 'ground_transit_business_days'
  | 'stock_truck_route'
  | 'freight_option'
>;

interface FulfillmentFieldsProps {
  value: Partial<FulfillmentValue>;
  onChange: (patch: Partial<FulfillmentValue>) => void;
  density?: 'standard' | 'compact';
  deadlineHelper?: React.ReactNode;
  deadlineHelperColor?: 'default' | 'warning';
}

const DAY_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export const FulfillmentFields: React.FC<FulfillmentFieldsProps> = ({
  value,
  onChange,
  density = 'standard',
  deadlineHelper,
  deadlineHelperColor = 'default',
}) => {
  const size = density === 'compact' ? 'small' : undefined;
  const fullWidth = density === 'standard';
  const switchSize = density === 'compact' ? 'small' : 'medium';

  const dayValue =
    value.stock_truck_delivery_day === null || value.stock_truck_delivery_day === undefined
      ? ''
      : value.stock_truck_delivery_day;

  const defaultDeadlineHelper = "Shown in the customer's quote email (e.g. Tuesday 2PM).";

  return (
    <>
      <FormControlLabel
        control={
          <Switch
            size={switchSize}
            name="show_reply_by_time"
            checked={value.show_reply_by_time ?? false}
            onChange={e => onChange({ show_reply_by_time: e.target.checked })}
          />
        }
        label="Show reply-by time"
      />

      {value.show_reply_by_time && (
        <TextField
          size={size}
          name="quote_confirmation_deadline"
          label="Quote Confirmation Deadline"
          type="text"
          value={value.quote_confirmation_deadline ?? ''}
          onChange={e => onChange({ quote_confirmation_deadline: e.target.value })}
          fullWidth={fullWidth}
          helperText={deadlineHelper ?? defaultDeadlineHelper}
          FormHelperTextProps={
            deadlineHelperColor === 'warning' ? { sx: { color: 'warning.main' } } : undefined
          }
        />
      )}

      <TextField
        size={size}
        name="collect_account_info"
        label="Collect Account Info"
        type="text"
        value={value.collect_account_info ?? ''}
        onChange={e => onChange({ collect_account_info: e.target.value })}
        fullWidth={fullWidth}
        helperText="Carrier account info displayed to the warehouse (e.g. UPS: 12345R, Zip: 65201)."
      />

      <FormControlLabel
        control={
          <Switch
            size={switchSize}
            name="uses_stock_truck"
            checked={value.uses_stock_truck ?? false}
            onChange={e => onChange({ uses_stock_truck: e.target.checked })}
          />
        }
        label="Stock Truck"
      />

      {value.uses_stock_truck ? (
        <FormControl size={size} fullWidth={fullWidth}>
          <InputLabel>Stock Truck Delivery Day</InputLabel>
          <Select
            name="stock_truck_delivery_day"
            value={dayValue}
            onChange={e =>
              onChange({
                stock_truck_delivery_day: e.target.value === '' ? null : Number(e.target.value),
              })
            }
            label="Stock Truck Delivery Day"
          >
            <MenuItem value="">Not Set</MenuItem>
            {DAY_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Day-of-week the FDT delivers to this location.</FormHelperText>
        </FormControl>
      ) : (
        <TextField
          size={size}
          name="ground_transit_business_days"
          label="Ground Transit Business Days"
          type="number"
          inputProps={{ min: 1, max: 10, step: 1 }}
          value={value.ground_transit_business_days ?? ''}
          onChange={e =>
            onChange({
              ground_transit_business_days: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          fullWidth={fullWidth}
          helperText="Carrier transit time in business days (e.g. 2)."
        />
      )}

      {value.uses_stock_truck && (
        <TextField
          size={size}
          name="stock_truck_route"
          label="Stock Truck Route"
          type="text"
          value={value.stock_truck_route ?? ''}
          onChange={e => onChange({ stock_truck_route: e.target.value })}
          fullWidth={fullWidth}
          helperText="e.g. Houston, Beaumont, DFW, etc."
        />
      )}

      <TextField
        size={size}
        name="freight_option"
        label="Freight Option"
        type="text"
        value={value.freight_option ?? ''}
        onChange={e => onChange({ freight_option: e.target.value })}
        fullWidth={fullWidth}
        helperText="This is the freight option that will be generated in HQ."
      />
    </>
  );
};
